// Modal de edição rápida do CLIENTE de um pedido. Aberto pelo lápis na
// lista de pedidos (Orders) — vendedora corrige nome, CPF/CNPJ, contato
// ou e-mail SEM precisar abrir o pedido completo.
//
// Caso de uso principal: CPF cadastrado errado bloqueia envio pro Bling.
// Valida dígito verificador no save com mensagem clara em vez do erro
// genérico do Bling.

import { useState } from 'react'
import { X, Save, AlertCircle } from 'lucide-react'
import { formatTelefone } from '../utils/fmt'
import { validarDocumento } from '../utils/validadores'

function maskDoc(digits, tipo) {
  const d = (digits || '').replace(/\D/g, '')
  if (tipo === 'CNPJ') {
    return d
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2')
      .slice(0, 18)
  }
  return d
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1-$2')
    .slice(0, 14)
}

export function EditarClienteModal({ pedido, onClose, onSalvar }) {
  const c = pedido?.cliente || {}
  const [nome, setNome]             = useState(c.nome || '')
  const [tipoDoc, setTipoDoc]       = useState(c.tipoDocumento || 'CPF')
  const [doc, setDoc]               = useState(c.documento || '')
  const [contato, setContato]       = useState(c.contato || '')
  const [email, setEmail]           = useState(c.email || '')
  const [erro, setErro]             = useState(null)

  function handleSalvar() {
    setErro(null)
    if (!nome.trim()) {
      setErro({ campo: 'nome', msg: 'Nome obrigatório' })
      return
    }
    const docDigits = doc.replace(/\D/g, '')
    if (docDigits && !validarDocumento(docDigits, tipoDoc)) {
      setErro({ campo: 'doc', msg: `${tipoDoc} inválido — confira os dígitos` })
      return
    }
    const atualizado = {
      ...pedido,
      cliente: {
        ...c,
        nome:            nome.trim(),
        tipoDocumento:   tipoDoc,
        documento:       docDigits,
        contato:         contato.replace(/\D/g, ''),
        email:           email.trim(),
      },
    }
    onSalvar(atualizado)
  }

  if (!pedido) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-3 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-slate-800">Editar cliente</h3>
            <p className="text-xs text-slate-400">Pedido #{pedido.numero}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">

          {/* Nome */}
          <div>
            <label className="label">Nome completo *</label>
            <input
              type="text"
              value={nome}
              onChange={e => setNome(e.target.value)}
              className={`input ${erro?.campo === 'nome' ? 'input-error' : ''}`}
              placeholder="Nome do cliente"
            />
            {erro?.campo === 'nome' && <p className="error-msg">{erro.msg}</p>}
          </div>

          {/* Tipo de documento */}
          <div>
            <label className="label">Tipo de documento</label>
            <div className="flex gap-2">
              {['CPF', 'CNPJ'].map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => { setTipoDoc(t); setErro(null) }}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold border-2 transition-colors
                    ${tipoDoc === t
                      ? 'border-[#111111] bg-[#111111] text-white'
                      : 'border-slate-200 text-slate-600 hover:border-slate-400'}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Documento */}
          <div>
            <label className="label">{tipoDoc}</label>
            <input
              type="text"
              value={maskDoc(doc, tipoDoc)}
              onChange={e => { setDoc(e.target.value.replace(/\D/g, '')); setErro(null) }}
              className={`input ${erro?.campo === 'doc' ? 'input-error' : ''}`}
              placeholder={tipoDoc === 'CNPJ' ? '00.000.000/0001-00' : '000.000.000-00'}
              inputMode="numeric"
            />
            {erro?.campo === 'doc' && (
              <div className="flex items-start gap-1.5 mt-1">
                <AlertCircle size={12} className="text-red-500 mt-0.5 shrink-0" />
                <p className="text-xs text-red-600">{erro.msg}</p>
              </div>
            )}
          </div>

          {/* Contato */}
          <div>
            <label className="label">Telefone / WhatsApp</label>
            <input
              type="text"
              value={formatTelefone(contato)}
              onChange={e => setContato(e.target.value)}
              className="input"
              placeholder="(83) 99999-9999"
              inputMode="tel"
            />
          </div>

          {/* E-mail */}
          <div>
            <label className="label">E-mail</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="input"
              placeholder="cliente@email.com"
              inputMode="email"
            />
          </div>

          <p className="text-[11px] text-slate-400 leading-relaxed">
            Pra editar endereço, móveis, valores e outras infos do pedido,
            abra o pedido pelo botão de detalhes.
          </p>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-slate-100 px-5 py-3 flex gap-3">
          <button onClick={onClose} className="btn-secondary flex-1 justify-center">
            Cancelar
          </button>
          <button onClick={handleSalvar} className="btn-primary flex-1 justify-center">
            <Save size={14} />
            Salvar
          </button>
        </div>
      </div>
    </div>
  )
}
